import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { BASE_URL, API } from "../api";
import { FiSearch, FiRefreshCw, FiEye, FiX, FiClipboard } from "react-icons/fi";
import { toasts } from "../components/AppToast";
import Pagination from "../components/Pagination";

export default function ManageDeletedOnlineBooking() {
  // STATE
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState("0");
  const [status, setStatus] = useState("m");

  const [page, setPage] = useState(1);
  const [PER] = useState(10);
  const [total, setTotal] = useState(0);

  const [selectedBooking, setSelectedBooking] = useState(null);
  // TOTAL PAGES
  const totalPages = Math.ceil(total / PER) || 1;
  // FETCH DELETED BOOKINGS
  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);

      const response = await API.get("/del-online/deleted", {
        params: {
          page,
          limit: PER,
          search: search.trim(),
          period,
          status,
        },
      });

      if (response.data?.success) {
        setBookings(response.data.data || []);
        setTotal(Number(response.data.total || 0));
      } else {
        setBookings([]);
        setTotal(0);
        toasts.error(
          response.data?.message || "Unable to fetch deleted bookings"
        );
      }
    } catch (error) {
      console.error("Fetch deleted bookings error:", error);
      setBookings([]);
      setTotal(0);
      toasts.error(
        error?.response?.data?.message || "Unable to fetch deleted bookings"
      );
    } finally {
      setLoading(false);
    }
  }, [page, PER, search, period, status]);


  // INITIAL LOAD / FILTER CHANGE


  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);


  // SEARCH / FILTERS


  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePeriodChange = (e) => {
    setPeriod(e.target.value);
    setPage(1);
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearch("");
    setPage(1);
  };


  // HELPERS


  const formatDate = (dateValue) => {
    if (!dateValue) return "-";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "-";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    return amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getPaymentStatus = (booking) => {
    const payStatus = Number(booking?.pay_status);
    const paymentType = Number(booking?.payment_type);

    if (payStatus === 1) {
      return {
        text: paymentType === 1 ? "Paid(P)" : "Paid(I)",
        className: "status-text text-active",
      };
    }

    return {
      text: "Unpaid",
      className: "status-text text-danger",
    };
  };

  const getBookingName = (booking) => {
    const title = booking?.online_title || "";
    const firstName = booking?.online_firstname || "";
    const lastName = booking?.online_lastname || "";
    return [title, firstName, lastName].filter(Boolean).join(" ");
  };


  // VIEW / RESTORE


  const handleView = async (bookingId) => {
    try {
      if (!bookingId) {
        toasts.error("Booking ID is missing");
        return;
      }

      setViewLoading(true);
      setSelectedBooking(null);

      const response = await API.get(`/del-online/deleted/${bookingId}`);

      if (!response.data?.success) {
        throw new Error(
          response.data?.message || "Unable to fetch deleted booking"
        );
      }

      setSelectedBooking(response.data.data);
    } catch (error) {
      console.error("View deleted booking error:", error);
      toasts.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to fetch deleted booking"
      );
      setSelectedBooking(null);
    } finally {
      setViewLoading(false);
    }
  };

  const handleRestore = async (bookingId) => {
    if (!bookingId) {
      toasts.error("Invalid booking ID");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to restore this deleted booking?"
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await API.put(`/del-online/restore/${bookingId}`);

      if (!response.data?.success) {
        throw new Error(response.data?.message || "Unable to restore booking");
      }

      toasts.success(response.data.message || "Booking restored successfully");
      setSelectedBooking(null);
      fetchBookings();
    } catch (error) {
      console.error("Restore deleted booking error:", error);
      toasts.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to restore booking"
      );
    } finally {
      setLoading(false);
    }
  };

  const closeView = () => {
    setSelectedBooking(null);
  };

 // =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (
      loading ||
      page <= 1
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.max(
          1,
          prev - 1
        )
    );
  };

  // =========================================================
  // PAGINATION - NEXT
  // =========================================================

  const handleNext = () => {
    if (
      loading ||
      totalPages <= 0 ||
      page >= totalPages
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.min(
          totalPages,
          prev + 1
        )
    );
  };


  // RENDER


  return (
    <Layout activePage="Manage Deleted Online Booking">
   

      <main className="dashboard-main">
        <div className="table-card">
          {/* PAGE HEADER */}
          <div className="table-card-head">
            <h3 className="table-card-title d-flex align-items-center">
              <FiClipboard size={26} className="me-2" />
              <span>Manage Deleted Online Booking</span>
              <span className="rec-count ms-1">{total} records</span>
            </h3>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="um-btn-secondary btn-sm"
                onClick={fetchBookings}
                disabled={loading}
              >
                <FiRefreshCw className="me-1" />
                Refresh
              </button>
            </div>
          </div>

          {/* FILTERS PANEL */}
          <div className="p-3 border-bottom bg-light bg-opacity-25">
            <div className="row g-3 align-items-end">
              <div className="col-xl-3 col-md-4 col-12">
                <label className="form-label fw-semibold small">
                  Search Period
                </label>
                <select
                  className="form-select season-input"
                  value={period}
                  onChange={handlePeriodChange}
                >
                  <option value="0">— All Periods —</option>
                  <option value="1">Today</option>
                  <option value="2">Last 7 Days</option>
                  <option value="3">Last 30 Days</option>
                </select>
              </div>

              <div className="col-xl-3 col-md-4 col-12">
                <label className="form-label fw-semibold small">Status</label>
                <select
                  className="form-select season-input"
                  value={status}
                  onChange={handleStatusChange}
                >
                  <option value="m">— All Status —</option>
                  <option value="1">Paid</option>
                  <option value="0">Unpaid</option>
                </select>
              </div>

              <div className="col-xl-6 col-md-4 col-12">
                <label className="form-label fw-semibold small">
                  Search Keyword
                </label>
                <div className="search-wrapper w-100">
                  <span className="search-icon">
                    <FiSearch />
                  </span>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search family name / email / booking reference..."
                    value={search}
                    onChange={handleSearch}
                  />
                  {search && (
                    <button
                      type="button"
                      className="season-clear-btn"
                      onClick={handleClearSearch}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* TABLE (NO HORIZONTAL SCROLLBAR, EXACT PERCENT WIDTHS) */}
          <div>
            <table
              className="table table-striped table-hover table-bordered align-middle mb-0"
              style={{
                tableLayout: "fixed",
                width: "100%",
                fontSize: "12.5px",
              }}
            >
              <thead className="table-light">
                <tr className="um-table-head text-center">
                  <th style={{ width: "5%" }} className="text-center p-2">
                    #
                  </th>
                  <th style={{ width: "20%" }} className="text-center p-2">
                    Booking Ref. No.
                  </th>
                  <th style={{ width: "22%" }} className="text-center p-2">
                    Name
                  </th>
                  <th style={{ width: "25%" }} className="text-center p-2">
                    Email ID
                  </th>
                  <th style={{ width: "13%" }} className="text-center p-2">
                    Pick Up Date
                  </th>
                  <th style={{ width: "9%" }} className="text-center p-2">
                    Status
                  </th>
                  <th style={{ width: "6%" }} className="text-center p-2">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 7 }).map((_, j) => (
                        <td key={j} className="text-center p-2">
                          <div className="skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : bookings.length > 0 ? (
                  bookings.map((booking, index) => {
                    const paymentStatus = getPaymentStatus(booking);
                    const isModified = Number(booking.is_modified) === 1;

                    return (
                      <tr key={booking.bookingid || index} className="um-row">
                        <td className="text-center p-2 text-muted">
                          {(page - 1) * PER + index + 1}
                        </td>

                        <td
                          className="text-center p-2"
                          style={{ wordBreak: "break-word" }}
                        >
                          <strong className={isModified ? "text-danger" : ""}>
                            {booking.fullbooking_no || booking.bookingid || "-"}
                          </strong>
                        </td>

                        <td
                          className="text-center p-2"
                          style={{ wordBreak: "break-word" }}
                        >
                          {getBookingName(booking) || "—"}
                        </td>

                        <td
                          className="text-center p-2"
                          style={{ wordBreak: "break-all" }}
                        >
                          {booking.online_emailid || "-"}
                        </td>

                        <td className="text-center p-2 text-nowrap">
                          {formatDate(booking.online_pickdate)}
                        </td>

                        <td className="um-td text-center p-2">
                          <div className="status-wrap justify-content-center m-0">
                            <span
                              className={paymentStatus.className}
                              style={{ fontSize: "11px" }}
                            >
                              {paymentStatus.text}
                            </span>
                          </div>
                        </td>

                        <td className="p-2 text-center">
                          <div className="action-buttons d-flex justify-content-center">
                            <button
                              type="button"
                              className="action-btn action-view btn btn-sm p-1"
                              title="View Deleted Booking"
                              onClick={() => handleView(booking.bookingid)}
                            >
                              <FiEye size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-5">
                      <div className="empty-icon">
                        <FiSearch size={22} />
                      </div>
                      <div className="empty-title">No records Available</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* PAGINATION */}
            <Pagination
  page={page}
  totalPages={totalPages}
  total={total}
  limit={10}
  loading={loading}
  onPrevious={handlePrevious}
  onNext={handleNext}
/>
          </div>
        </div>
      </main>

      {/* VIEW DELETED BOOKING MODAL */}
      {selectedBooking && (
        <div className="um-overlay" onClick={closeView}>
          <div
            className="um-modal um-view-modal modal-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* MODAL HEADER */}
            <div className="um-modal-head">
              <h2 className="um-modal-title">View Deleted Online Booking</h2>
              <button type="button" className="um-x-btn" onClick={closeView}>
                <FiX />
              </button>
            </div>

            {/* MODAL BODY */}
            {viewLoading ? (
              <div className="um-modal-scroll">
                <div className="text-center py-5">
                  <div className="spinner" />
                  <div className="mt-3 text-muted">
                    Loading booking details...
                  </div>
                </div>
              </div>
            ) : (
              (() => {
                const booking = selectedBooking.booking || {};
                const personal = selectedBooking.personalDetails || {};
                const itinerary = selectedBooking.itinerary || {};
                const car = selectedBooking.car || {};
                const calculations = selectedBooking.calculations || {};
                const payment = selectedBooking.payment || {};
                const meta = selectedBooking.meta || {};
                const regularClient = selectedBooking.regularClient;
                const specialDiscount = selectedBooking.specialDiscount;
                const freeExtras = selectedBooking.freeExtras || [];
                const paidExtras = selectedBooking.paidExtras || [];
                const freeInsurance = selectedBooking.freeInsurance || [];
                const paidInsurance = selectedBooking.paidInsurance || [];

                return (
                  <>
                    <div className="um-modal-scroll">
                      {/* HEADER SUMMARY */}
                      <div className="profile-header mb-4">
                        <div className="row g-0 w-100">
                          <div className="col-md-4 p-3 border-end">
                            <div className="form-label fw-semibold">
                              Booking Ref. No.
                            </div>
                            <div className="profile-row-value text-danger fw-bold">
                              {meta.bookingReference ||
                                booking.fullbooking_no ||
                                meta.bookingId ||
                                "—"}
                            </div>
                          </div>

                          <div className="col-md-4 p-3 border-end">
                            <div className="form-label fw-semibold">
                              Booking Date
                            </div>
                            <div className="profile-row-value">
                              {formatDate(
                                meta.bookingDate || booking.online_bookingdate
                              )}
                            </div>
                          </div>

                          <div className="col-md-4 p-3">
                            <div className="form-label fw-semibold">
                              Payment Status
                            </div>
                            <div
                              className={`profile-row-value ${
                                Number(payment.paymentStatus) === 1
                                  ? "text-success fw-bold"
                                  : "text-danger fw-bold"
                              }`}
                            >
                              {Number(payment.paymentStatus) === 1
                                ? "Paid"
                                : "Unpaid"}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* PERSONAL INFORMATION */}
                      <div className="sec-div">
                        <span>PERSONAL INFORMATION</span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-3">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Name</div>
                              <div className="profile-row-value">
                                {personal.title || "—"}{" "}
                                {personal.firstName || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Family Name</div>
                              <div className="profile-row-value">
                                {personal.lastName || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Email ID</div>
                              <div className="profile-row-value">
                                {personal.email || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Telephone</div>
                              <div className="profile-row-value">
                                {personal.phone || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Mobile Number</div>
                              <div className="profile-row-value">
                                {personal.mobile || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Gender</div>
                              <div className="profile-row-value">
                                {personal.gender || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Country</div>
                              <div className="profile-row-value">
                                {personal.country || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Address</div>
                              <div className="profile-row-value">
                                {personal.address || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-2">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Zip Code</div>
                              <div className="profile-row-value">
                                {personal.postcode || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">City</div>
                              <div className="profile-row-value">
                                {personal.city || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Pick Location Details :
                              </div>
                              <div className="profile-row-value">
                                {itinerary.pickupDetails || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Drop Location Details :
                              </div>
                              <div className="profile-row-value">
                                {itinerary.dropDetails || "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ITINERARY DETAILS */}
                      <div className="sec-div mt-4">
                        <span>ITINERARY DETAILS</span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Pick Up Location</div>
                              <div className="profile-row-value">
                                {itinerary.pickupLocation || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Pick Up Date</div>
                              <div className="profile-row-value">
                                {formatDate(itinerary.pickupDate)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Pick Up Time</div>
                              <div className="profile-row-value">
                                {itinerary.pickupTime || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Return Location</div>
                              <div className="profile-row-value">
                                {itinerary.returnLocation || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Return Date</div>
                              <div className="profile-row-value">
                                {formatDate(itinerary.returnDate)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Return Time</div>
                              <div className="profile-row-value">
                                {itinerary.returnTime || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Total Duration</div>
                              <div className="profile-row-value">
                                {itinerary.durationDays || 0} Days
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Duration Hours</div>
                              <div className="profile-row-value">
                                {itinerary.durationHours || 0} Hours
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Driver Age</div>
                              <div className="profile-row-value">
                                {personal.driverAge || "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* CAR DETAILS */}
                      <div className="sec-div mt-4">
                        <span>CAR DETAILS</span>
                      </div>

                      {car ? (
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="profile-row">
                              <div>
                                <div className="form-label">Car Model Name</div>
                                <div className="profile-row-value">
                                  {car.name || "—"}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-2">
                            <div className="profile-row">
                              <div>
                                <div className="form-label">Passenger</div>
                                <div className="profile-row-value">
                                  {car.passenger ?? "—"}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-2">
                            <div className="profile-row">
                              <div>
                                <div className="form-label">Luggage</div>
                                <div className="profile-row-value">
                                  {car.luggage ?? "—"}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-1">
                            <div className="profile-row">
                              <div>
                                <div className="form-label">Doors</div>
                                <div className="profile-row-value">
                                  {car.doors ?? "—"}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-1">
                            <div className="profile-row">
                              <div>
                                <div className="form-label">A.C.</div>
                                <div className="profile-row-value">
                                  {car.airCondition ?? "—"}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-3">
                            <div className="profile-row">
                              <div>
                                <div className="form-label">
                                  Engine Capacity
                                </div>
                                <div className="profile-row-value">
                                  {car.engineCapacity || "—"}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="profile-row">
                              <div>
                                <div className="form-label">Car Image</div>
                                <div className="profile-row-value">
                                  {car.image ? (
                                    <img
                                      src={`${BASE_URL}/${car.image}`}
                                      alt={car.image_alt || car.image}
                                      style={{
                                        width: "50%",
                                        borderRadius: "6px",
                                      }}
                                      onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                      }}
                                    />
                                  ) : (
                                    "—"
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-muted">
                          No car details available.
                        </div>
                      )}

                      {/* EXTRA DETAILS */}
                      {(freeExtras.length > 0 ||
                        paidExtras.length > 0 ||
                        freeInsurance.length > 0 ||
                        paidInsurance.length > 0) && (
                        <>
                          <div className="sec-div mt-4">
                            <span>EXTRA DETAILS</span>
                          </div>

                          {freeExtras.length > 0 && (
                            <div className="mb-4">
                              <div className="form-label fw-semibold mb-2">
                                Free Optional Extras
                              </div>

                              <div className="table-responsive">
                                <table className="table table-bordered table-striped table-sm align-middle">
                                  <thead className="table-light">
                                    <tr className="um-table-head">
                                      <th>Items Name</th>
                                      <th>Quantity</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {freeExtras.map((item, index) => (
                                      <tr key={item.id || index}>
                                        <td className="text-danger">
                                          {item.name || "—"}
                                        </td>
                                        <td className="text-danger">
                                          {item.quantity ?? 0}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {paidExtras.length > 0 && (
                            <div className="mb-4">
                              <div className="form-label fw-semibold mb-2">
                                Paid Optional Extras
                              </div>

                              <div className="table-responsive">
                                <table className="table table-bordered table-striped table-sm align-middle">
                                  <thead className="table-light">
                                    <tr className="um-table-head">
                                      <th>Items Name</th>
                                      <th>Quantity</th>
                                      <th>Rate/Day</th>
                                      <th>Days</th>
                                      <th>Max Rate</th>
                                      <th>Total Amount</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {paidExtras.map((item, index) => (
                                      <tr
                                        key={item.optdet_id || item.id || index}
                                      >
                                        <td className="text-danger">
                                          {item.optitem_name ||
                                            item.name ||
                                            "—"}
                                        </td>
                                        <td className="text-danger text-center">
                                          {item.quantity ??
                                            item.online_extraitemqty ??
                                            0}
                                        </td>
                                        <td className="text-danger">
                                          €{" "}
                                          {formatAmount(
                                            item.rate ?? item.opt_dayrate ?? 0
                                          )}
                                        </td>
                                        <td className="text-danger text-center">
                                          {item.days ??
                                            itinerary.durationDays ??
                                            0}
                                        </td>
                                        <td className="text-danger">
                                          €{" "}
                                          {formatAmount(
                                            item.maxRate ??
                                              item.opt_maxrate ??
                                              0
                                          )}
                                        </td>
                                        <td className="text-danger fw-semibold">
                                          €{" "}
                                          {formatAmount(item.totalAmount ?? 0)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {freeInsurance.length > 0 && (
                            <div className="mb-4">
                              <div className="form-label fw-semibold mb-2">
                                Free Insurance Details
                              </div>

                              <div className="table-responsive">
                                <table className="table table-bordered table-striped table-sm align-middle">
                                  <thead className="table-light">
                                    <tr className="um-table-head">
                                      <th>Items Name</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {freeInsurance.map((item, index) => (
                                      <tr key={item.id || index}>
                                        <td className="text-danger">
                                          {item.name || "—"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {paidInsurance.length > 0 && (
                            <div className="mb-4">
                              <div className="form-label fw-semibold mb-2">
                                Paid Insurance Details
                              </div>

                              <div className="table-responsive">
                                <table className="table table-bordered table-striped table-sm align-middle">
                                  <thead className="table-light">
                                    <tr className="um-table-head">
                                      <th>Items Name</th>
                                      <th>Rate/Day</th>
                                      <th>Days</th>
                                      <th>Total Amount</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {paidInsurance.map((item, index) => (
                                      <tr
                                        key={item.insdtl_id || item.id || index}
                                      >
                                        <td className="text-danger">
                                          {item.insitem_name ||
                                            item.name ||
                                            "—"}
                                        </td>
                                        <td className="text-danger">
                                          €{" "}
                                          {formatAmount(
                                            item.rate ||
                                              item.online_itemrate ||
                                              0
                                          )}
                                        </td>
                                        <td className="text-danger text-center">
                                          {item.days ??
                                            itinerary.durationDays ??
                                            0}
                                        </td>
                                        <td className="text-danger fw-semibold">
                                          €{" "}
                                          {formatAmount(item.totalAmount || 0)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* REGULAR CLIENT */}
                      {regularClient && (
                        <>
                          <div className="sec-div mt-4">
                            <span>REGULAR CLIENT DETAILS</span>
                          </div>

                          <div className="row g-3">
                            <div className="col-md-6">
                              <div className="profile-row">
                                <div>
                                  <div className="form-label">Client Id</div>
                                  <div className="profile-row-value">
                                    {regularClient.clientId || "—"}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="profile-row">
                                <div>
                                  <div className="form-label">Client Email</div>
                                  <div className="profile-row-value">
                                    {regularClient.clientEmail || "—"}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="profile-row">
                                <div>
                                  <div className="form-label">
                                    Discount on per day price
                                  </div>
                                  <div className="profile-row-value text-danger">
                                    {regularClient.discountPercent || 0} %
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="profile-row">
                                <div>
                                  <div className="form-label">
                                    Discount Amount
                                  </div>
                                  <div className="profile-row-value text-danger">
                                    €{" "}
                                    {formatAmount(regularClient.discountAmount)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      {/* SPECIAL DISCOUNT */}
                      {specialDiscount && (
                        <>
                          <div className="sec-div mt-4">
                            <span>SPECIAL DISCOUNT DETAILS</span>
                          </div>

                          <div className="row g-3">
                            <div className="col-md-6">
                              <div className="profile-row">
                                <div>
                                  <div className="form-label">
                                    Per day special discount amount
                                  </div>
                                  <div className="profile-row-value text-danger">
                                    €{" "}
                                    {formatAmount(
                                      specialDiscount.discountAmount
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      {/* RATE CHART DETAILS */}
                      <div className="sec-div mt-4">
                        <span>RATE CHART DETAILS</span>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-bordered table-striped align-middle">
                          <thead className="table-light">
                            <tr className="um-table-head">
                              <th>Quote</th>
                              <th>Calculation</th>
                              <th>Total Amount</th>
                            </tr>
                          </thead>

                          <tbody>
                            <tr>
                              <td className="text-danger">Rental Price</td>
                              <td className="text-danger">
                                € {formatAmount(calculations.rentalPrice)} ×{" "}
                                {calculations.rentalDays || 0} Days
                              </td>
                              <td className="text-danger">
                                € {formatAmount(calculations.rentalTotal)}
                              </td>
                            </tr>

                            {Number(calculations.extraHourCharge) > 0 && (
                              <tr>
                                <td className="text-danger">
                                  Extra Hour Delay Charges
                                  <br />
                                  (For {calculations.extraHours || 0} Hours)
                                </td>
                                <td>—</td>
                                <td className="text-danger">
                                  € {formatAmount(calculations.extraHourCharge)}
                                </td>
                              </tr>
                            )}

                            {Number(calculations.oneWayRentalCharges) > 0 && (
                              <tr>
                                <td className="text-danger">
                                  One Way Rental Charges
                                </td>
                                <td>—</td>
                                <td className="text-danger">
                                  €{" "}
                                  {formatAmount(
                                    calculations.oneWayRentalCharges
                                  )}
                                </td>
                              </tr>
                            )}

                            {Number(calculations.pickupCharge) > 0 && (
                              <tr>
                                <td className="text-danger">
                                  Pick Up Location Charges
                                </td>
                                <td>—</td>
                                <td className="text-danger">
                                  € {formatAmount(calculations.pickupCharge)}
                                </td>
                              </tr>
                            )}

                            {Number(calculations.dropoffCharge) > 0 && (
                              <tr>
                                <td className="text-danger">
                                  Drop Off Location Charges
                                </td>
                                <td>—</td>
                                <td className="text-danger">
                                  € {formatAmount(calculations.dropoffCharge)}
                                </td>
                              </tr>
                            )}

                            {Number(calculations.totalLocationPrice) > 0 && (
                              <tr>
                                <td className="text-danger">
                                  Total Location Charges
                                </td>
                                <td>—</td>
                                <td className="text-danger">
                                  €{" "}
                                  {formatAmount(
                                    calculations.totalLocationPrice
                                  )}
                                </td>
                              </tr>
                            )}

                            {Number(calculations.totalInsurance) > 0 && (
                              <tr>
                                <td className="text-danger">Total Insurance</td>
                                <td>—</td>
                                <td className="text-danger">
                                  € {formatAmount(calculations.totalInsurance)}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* TOTAL PRICE */}
                      <div className="row g-0 border mt-3">
                        <div className="col-md-6 p-3 fw-semibold">
                          Total Price
                        </div>
                        <div className="col-md-6 p-3 text-end text-danger fs-5 fw-bold">
                          € {formatAmount(calculations.totalPrice)}
                        </div>
                      </div>

                      {/* PAYMENT DETAILS */}
                      <div className="sec-div mt-4">
                        <span>PAYMENT DETAILS</span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Advance Paid</div>
                              <div className="profile-row-value text-danger">
                                {payment.advancePercent || 0} % of total price
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Final Total Rental Price
                              </div>
                              <div className="profile-row-value text-danger fw-bold">
                                € {formatAmount(calculations.totalPrice)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Advance Paid Amount
                              </div>
                              <div className="profile-row-value text-danger fw-bold">
                                − € {formatAmount(payment.advanceAmount)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Balance to Pay</div>
                              <div className="profile-row-value text-danger fw-bold">
                                € {formatAmount(payment.balanceToPay)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Payment Status</div>
                              <div className="profile-row-value">
                                {Number(payment.paymentStatus) === 1
                                  ? "Paid"
                                  : "Unpaid"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* MODAL FOOTER */}
                    <div className="um-modal-foot">
                      <button
                        type="button"
                        className="um-btn-primary"
                        onClick={() =>
                          handleRestore(
                            selectedBooking?.meta?.bookingId ||
                              selectedBooking?.booking?.bookingid
                          )
                        }
                        disabled={loading}
                      >
                        {loading ? "Restoring..." : "Restore"}
                      </button>
                      <button
                        type="button"
                        className="um-btn-secondary"
                        onClick={closeView}
                      >
                        Close
                      </button>
                    </div>
                  </>
                );
              })()
            )}
          </div>
        </div>
      )}
     
    </Layout>
  );
}
